/**
 * Turns a raw tool/event name from the reporting progress stream into an
 * analyst-voice label for the "narrated work" progress UI, e.g.
 * `run_sql` -> "Running the query".
 *
 * Labels are verb-first, first person / present continuous, no em dashes.
 * Unknown names fall back to a cleaned-up human phrase rather than raw
 * snake_case. An optional `detail` (e.g. a table name carried on the event)
 * is appended as " - <detail>".
 */

/** Maps a normalised snake_case tool name to an analyst-voice label. */
const TOOL_LABELS: Record<string, string> = {
  parse_question: 'Understanding the question',
  understand: 'Understanding the question',
  understand_question: 'Understanding the question',
  run_sql: 'Running the query',
  execute_sql: 'Running the query',
  query: 'Running the query',
  read_data_context: 'Reading business context',
  lookup_terms: 'Checking definitions',
  get_metric_sql: 'Checking definitions',
  get_definitions: 'Checking definitions',
  get_table_info: 'Reading the schema',
  inspect_tables: 'Reading the schema',
  search_schema: 'Reading the schema',
  get_schema: 'Reading the schema',
  validate: 'Validating the query',
  check_sql: 'Validating the query',
  validate_sql: 'Validating the query',
  read_custom_view: 'Reading the custom view',
  edit_custom_view: 'Updating the custom view',
};

/**
 * Normalise a raw tool name to a snake_case lookup key: strips any
 * `namespace__` prefix, splits camelCase, and collapses non-alphanumerics
 * to single underscores. `Run SQL`, `run-sql`, `runSql` all become `run_sql`.
 */
function normalizeKey(raw: string): string {
  const base = raw.includes('__') ? raw.slice(raw.lastIndexOf('__') + 2) : raw;
  return base
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Turn an unmapped tool name into a readable sentence-cased phrase: drop a
 * trailing `sql`/`tool` noise word, replace underscores with spaces, and
 * capitalise the first word. `fetch_offer_data` -> "Fetch offer data".
 */
function humanizeUnknown(raw: string): string {
  const base = raw.includes('__') ? raw.slice(raw.lastIndexOf('__') + 2) : raw;
  const words = base
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  // Drop a trailing noise word (e.g. `_sql`, `_tool`) unless it is all we have.
  if (words.length > 1 && (words[words.length - 1] === 'sql' || words[words.length - 1] === 'tool')) {
    words.pop();
  }

  if (words.length === 0) {
    return '';
  }

  words[0] = words[0][0].toUpperCase() + words[0].slice(1);
  return words.join(' ');
}

/**
 * Format a progress step label for the reporting AI ask stream.
 *
 * @param raw    The raw tool/event name (e.g. `run_sql`).
 * @param detail Optional detail carried on the event (e.g. a table name),
 *               appended as " - <detail>".
 */
export function formatLeDataToolLabel(raw: string, detail?: string): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) {
    return '';
  }

  const label = TOOL_LABELS[normalizeKey(trimmed)] ?? humanizeUnknown(trimmed);
  if (!label) {
    return '';
  }

  const trimmedDetail = detail?.trim();
  return trimmedDetail ? `${label} - ${trimmedDetail}` : label;
}
